'use client';

import { useEffect, useState } from 'react';

function idDeHash(hash: string): string | null {
    if (hash.length <= 1) return null;
    try {
        return decodeURIComponent(hash.slice(1));
    } catch {
        // Un hash mal codificado (%E0%A4%A) no nombra ninguna pregunta: se ignora en vez de romper la página.
        return null;
    }
}

function comportamientoDeDesplazamiento(): ScrollBehavior {
    const sinMovimiento = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return sinMovimiento ? 'auto' : 'smooth';
}

/**
 * Abre la pregunta que nombra el hash de la URL (/ayuda#setup-1) y se desplaza a ella, tanto al cargar como
 * cuando el hash cambia con la página abierta (D-27). Un hash que no coincide con ninguna pregunta conocida
 * no hace nada: solo se actúa sobre ids de la lista, nunca sobre un selector construido con lo que venga en la URL.
 *
 * @param idsConocidos ids de las preguntas que existen
 * @param alAbrir debe ser estable entre renderizados; se encarga de mostrar la pregunta (quitar filtros, abrirla)
 */
export function useFaqDesdeHash(idsConocidos: readonly string[], alAbrir: (id: string) => void): void {
    const [idPorDesplazar, setIdPorDesplazar] = useState<string | null>(null);

    useEffect(() => {
        const abrirLaDelHash = () => {
            const id = idDeHash(window.location.hash);
            if (id === null || !idsConocidos.includes(id)) return;
            alAbrir(id);
            setIdPorDesplazar(id);
        };
        abrirLaDelHash();
        window.addEventListener('hashchange', abrirLaDelHash);
        return () => window.removeEventListener('hashchange', abrirLaDelHash);
    }, [idsConocidos, alAbrir]);

    useEffect(() => {
        if (idPorDesplazar === null) return;
        document.getElementById(idPorDesplazar)?.scrollIntoView({ behavior: comportamientoDeDesplazamiento(), block: 'start' });
        setIdPorDesplazar(null);
    }, [idPorDesplazar]);
}
